import { useRef, useState, useCallback } from 'react';
import { SongProgressTracker } from '../tracking/SongProgressTracker';
import { AdPolicy } from '../policy/AdPolicy';
import { adsterraAdapter } from '../adsterra/AdsterraAdapter';
import { AdAnalyticsService, AD_EVENTS } from '../analytics/AdAnalyticsService';
import { AD_FLAGS } from '../adsterra/adsterraConfig';

/**
 * Orchestrates the full ad break flow.
 *
 * Usage in PlaySession.js:
 *
 *   const {
 *     isAdBreakOpen,
 *     completedSongCount,
 *     handleSongCompleted,
 *     handleContinueAdBreak,
 *     handleMaybeLater,
 *   } = useAdBreakFlow({ isPremiumUser, sessionId, userId, onResume });
 *
 * - Call handleSongCompleted() ONLY when a song ends naturally (not on skip/error).
 * - onResume() is called after the ad break is dismissed (Continue or Maybe Later).
 */
export function useAdBreakFlow({ isPremiumUser, sessionId, userId, onResume }) {
  const trackerRef = useRef(new SongProgressTracker());
  const policyRef  = useRef(new AdPolicy());
  const analyticsRef = useRef(new AdAnalyticsService({ sessionId, userId }));

  const [isAdBreakOpen, setIsAdBreakOpen] = useState(false);
  const [completedSongCount, setCompletedSongCount] = useState(0);

  const handleSongCompleted = useCallback(() => {
    const tracker = trackerRef.current;
    const policy  = policyRef.current;
    const analytics = analyticsRef.current;

    const count = tracker.recordCompletedSong();
    setCompletedSongCount(count);
    analytics.track(AD_EVENTS.SONG_COMPLETED, { completedSongCount: count });

    if (policy.canShowAdBreak({ isPremiumUser, completedSongCount: count })) {
      analytics.track(AD_EVENTS.AD_BREAK_DUE, { completedSongCount: count });
      setIsAdBreakOpen(true);
      analytics.track(AD_EVENTS.AD_BREAK_MODAL_SHOWN);
    } else {
      // No ad break — resume flow immediately
      onResume?.();
    }
  }, [isPremiumUser, onResume]);

  const handleContinueAdBreak = useCallback(async () => {
    const tracker  = trackerRef.current;
    const policy   = policyRef.current;
    const analytics = analyticsRef.current;

    analytics.track(AD_EVENTS.AD_BREAK_CONTINUE_CLICKED);
    analytics.track(AD_EVENTS.AD_TRIGGER_ATTEMPT, { mode: AD_FLAGS.defaultAdMode });

    const result = await adsterraAdapter.triggerAd(AD_FLAGS.defaultAdMode);

    if (result.success) {
      analytics.track(AD_EVENTS.AD_TRIGGER_SUCCESS, { mode: result.mode });
      tracker.reset();
      setCompletedSongCount(0);
      analytics.track(AD_EVENTS.SONG_COUNTER_RESET);
      policy.recordAdShown();
    } else {
      const eventType = result.error === 'popup_blocked'
        ? AD_EVENTS.POPUP_BLOCKED
        : AD_EVENTS.AD_TRIGGER_FAILED;
      analytics.track(eventType, { error: result.error, mode: result.mode });
      // Still reset counter so we don't spam retries
      tracker.reset();
      setCompletedSongCount(0);
    }

    setIsAdBreakOpen(false);
    analytics.track(AD_EVENTS.PLAYLIST_RESUMED);
    onResume?.();
  }, [onResume]);

  const handleMaybeLater = useCallback(() => {
    const analytics = analyticsRef.current;
    analytics.track(AD_EVENTS.AD_BREAK_LATER_CLICKED);
    // Snooze: don't reset counter, just close and let policy cooldown prevent
    // the next ad from firing too soon.
    setIsAdBreakOpen(false);
    analytics.track(AD_EVENTS.PLAYLIST_RESUMED);
    onResume?.();
  }, [onResume]);

  return {
    isAdBreakOpen,
    completedSongCount,
    handleSongCompleted,
    handleContinueAdBreak,
    handleMaybeLater,
  };
}
